/* import __COLOCATED_TEMPLATE__ from './adc-select.hbs'; */
import Component from '@ember/component';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { alias } from '@ember/object/computed';
import { htmlSafe } from '@ember/template';

import Comparator from '@ebiz/ember-aditools-device-table/utils/comparator';
import PartialTextFilter from '@ebiz/ember-aditools-device-table/utils/partial-text-filter';
import EngineeringFilter from '@ebiz/ember-aditools-device-table/utils/engineering-filter';
import SelectionFilter from '@ebiz/ember-aditools-device-table/utils/selection-filter';

import { tracked } from '@glimmer/tracking';
import { eng3 } from '@ebiz/pretty-num';

import Constants from '../utils/constants';

export default class AdcSelect extends Component {
    tagName = '';

    @service('aditools-analytics') analytics;
    @service('aditools-site') site;
    @service('data') model;
    @service intl;
    @service urlData;
    @service selectionData;
    @service sigmaDeltaData;
    @service driverService;
    @service driverSupplyService;
    @service hashDataService;
    @service partData;

    @alias('selectionData.selectedAdc') selectedItem;
    @alias('selectionData.previousAdc') previousItem;

    @tracked locale = this.site.AdiLocale;
    @tracked selectableAdcOptions = this.model.selectableAdcOptions;
    @tracked selectedPart = '';

    @tracked pstLinks = [
        {
            href: {
                all: `${this.urlData.analogBaseParametricSearchUrl}/${Constants.URL.AD_CONVERTERS}`,
                inThisTool: `${this.urlData.analogBaseParametricSearchUrl}/${Constants.URL.AD_CONVERTERS}#/p0=${this.urlData.adcFilterString}`,
            },
            name: {
                all: this.intl.t('parametric_search.ad_converters.all'),
                inThisTool: this.intl.t('parametric_search.ad_converters.in_this_tool'),
            },
        },
    ];

    @tracked htmlGridNotes = htmlSafe(
        this.intl.t('messages.adc_note', {
            footnote: this.intl.t('footnote'),
            freq: `${eng3(this.model.frequency, { separatedUnit: true })}${this.intl.t('units.hz')}`,
            rext: `${eng3(this.model.rext, { separatedUnit: true })}${this.intl.t('units.ohms')}`,
        }),
    );

    @tracked options = {
        asyncEditorLoading: true,
        showHeaderRow: true,
        resizable: true,
        forceFitColumns: false,
        multiSelect: false,
        headerRowHeight: 30,
        rowHeight: 25,
        autoHeight: false,
        enableColumnReorder: false,
        multiColumnSort: true,
        frozenColumn: this.frozenColumn || 0,
        explicitInitialization: true,
    };
    @tracked data = [];
    @tracked columns = [];

    ////////////////////////////////////////////////////////////////////////////////
    // User Functions                                                             //
    ////////////////////////////////////////////////////////////////////////////////
    populateRows() {
        this.data = [];
        this.data = this.selectableAdcOptions.map((row) => {
            return {
                id: row.part,
                part: row.part,
                bits: eng3(row.bits),
                maxSampleRate: eng3(row.maxSampleRate),
                inputType: row.inputType,
                inputPolarity: row.inputPolarity,
                ADCSNR: eng3(row.ADCSNR),
                thd: eng3(row.thd),
                vrefMax: eng3(row.vrefMax),
                vrefMin: eng3(row.vrefMin),
                price: eng3(row.price),
                channels: eng3(row.channels),
                package: row.package,
                datasheet: row.datasheet, // for link in the field "id"
                vintage: row.id, // for default sort
            };
        });
    }

    constructColumns() {
        let linkFormatter = function (row, cell, value, columnDef, dataContext) {
            return [
                '<span class="',
                value.toLowerCase(),
                '-part-number-cell part-number-cell">',
                value,
                '<a target="_blank"',
                'class="recommended-part-item"',
                'rel="noopener noreferrer"',
                'data-action="part"',
                'data-partid="',
                value,
                '"',
                ' href="',
                dataContext.datasheet,
                '">',
                '<span class="link-icon"></span>',
                '</a>',
                '</span>',
            ].join('');
        };

        this.columns = [
            {
                id: 'part',
                name: this.intl.t('column_headers.device'),
                field: 'part',
                width: 115,
                sortable: true,
                formatter: linkFormatter,
                filter: new PartialTextFilter('', 'part'),
                toolTip: this.intl.t('column_headers.device'),
            },
            {
                id: 'bits',
                name: `${this.intl.t('column_headers.resolution')} (${this.intl.t('units.bits')})`,
                field: 'bits',
                width: 140,
                sortable: true,
                comparator: Comparator.engineeringComparator,
                filter: new EngineeringFilter('GT', '', 'bits'),
                toolTip: `${this.intl.t('column_headers.resolution')} (${this.intl.t('units.bits')})`,
            },
            {
                id: 'maxSampleRate',
                name: `${this.intl.t('column_headers.sampling_rate')} (${this.intl.t('units.sps')})`,
                field: 'maxSampleRate',
                width: 155,
                sortable: true,
                comparator: Comparator.engineeringComparator,
                filter: new EngineeringFilter('GT', '', 'maxSampleRate'),
                toolTip: `${this.intl.t('column_headers.sampling_rate')} (${this.intl.t('units.sps')})`,
            },
            {
                id: 'inputType',
                name: this.intl.t('column_headers.input_type'),
                field: 'inputType',
                width: 150,
                sortable: true,
                filter: new SelectionFilter([
                    { value: '', display: 'All' },
                    { value: 'Differential', match: 'Differential', display: 'Differential' },
                    { value: 'Pseudo Diff', match: 'Pseudo Diff', display: 'Pseudo Diff' },
                ]),
                toolTip: this.intl.t('column_headers.input_type'),
            },
            {
                id: 'inputPolarity',
                name: this.intl.t('column_headers.input_polarity'),
                field: 'inputPolarity',
                width: 170,
                sortable: true,
                filter: new PartialTextFilter('', 'inputPolarity'),
                toolTip: this.intl.t('column_headers.input_polarity'),
            },
            {
                id: 'snr',
                name: `${this.intl.t('column_headers.snr')} (${this.intl.t('units.db')})`,
                field: 'ADCSNR',
                width: 115,
                sortable: true,
                comparator: Comparator.engineeringComparator,
                filter: new EngineeringFilter('GT', '', 'ADCSNR'),
                toolTip: `${this.intl.t('column_headers.snr')} (${this.intl.t('units.db')})`,
            },

            {
                id: 'thd',
                name: `${this.intl.t('column_headers.thd')} (${this.intl.t('units.db')})
                ${this.intl.t('sup', {
                    val: this.intl.t('footnote'),
                })}
                `,
                field: 'thd',
                width: 115,
                sortable: true,
                comparator: Comparator.engineeringComparator,
                filter: new EngineeringFilter('GT', '', 'thd'),
                toolTip: `${this.intl.t('column_headers.thd')} (${this.intl.t('units.db')})`,
            },
            {
                id: 'vrefMax',
                name: `${this.intl.t('column_headers.vref_max')} (${this.intl.t('units.v')})`,
                field: 'vrefMax',
                width: 115,
                sortable: true,
                comparator: Comparator.engineeringComparator,
                filter: new EngineeringFilter('GT', '', 'vrefMax'),
                toolTip: `${this.intl.t('column_headers.vref_max')} (${this.intl.t('units.v')})`,
            },
            {
                id: 'vrefMin',
                name: `${this.intl.t('column_headers.vref_min')} (${this.intl.t('units.v')})`,
                field: 'vrefMin',
                width: 115,
                sortable: true,
                comparator: Comparator.engineeringComparator,
                filter: new EngineeringFilter('GT', '', 'vrefMin'),
                toolTip: `${this.intl.t('column_headers.vref_min')} (${this.intl.t('units.v')})`,
            },
            {
                id: 'price',
                name: `${this.intl.t('column_headers.price')} (${this.intl.t('units.price')})`,
                field: 'price',
                width: 130,
                sortable: true,
                comparator: Comparator.engineeringComparator,
                filter: new EngineeringFilter('GT', '', 'price'),
                toolTip: `${this.intl.t('column_headers.price')} (${this.intl.t('units.price')})`,
            },
            {
                id: 'channels',
                name: this.intl.t('column_headers.adc_channels'),
                field: 'channels',
                width: 130,
                sortable: true,
                comparator: Comparator.engineeringComparator,
                filter: new EngineeringFilter('GT', '', 'channels'),
                toolTip: this.intl.t('column_headers.adc_channels'),
            },
            {
                id: 'package',
                name: this.intl.t('column_headers.package'),
                field: 'package',
                width: 200,
                sortable: false,
                filter: new PartialTextFilter('', 'package'),
                toolTip: this.intl.t('column_headers.package'),
            },
        ];
    }

    ////////////////////////////////////////////////////////////////////////////////
    // Component Actions                                                          //
    ////////////////////////////////////////////////////////////////////////////////
    @action insertingGrid() {
        this.populateRows();
        this.constructColumns();

        const selectedItem = this.selectedItem.part.split('|')[0];
        this.selectedPart = this.data.find((item) => item.part === selectedItem).part;
    }

    @action resetFilters() {
        this.insertingGrid();

        this.analytics.setWebEvent(this.partData.toolName, 'Reset Filters', `Selected adc: ${this.selectedPart}`);
    }

    @action selectItem(closeDialog) {
        const selectedAdc = this.selectableAdcOptions.find((adc) => adc.part === this.selectedPart);
        this.previousItem = this.selectionData.selectedAdc;
        this.selectedItem = selectedAdc;

        // Handle adc variant processing
        this.model.updateShowVariant();
        this.model.switchVariant();
        this.sigmaDeltaData.refreshSigmaDeltaDefaults();

        /**
         * This is where the driver is reset.
         * Anything that relies on the selectedDriver must
         * be run after this.
         */
        this.model.setDefaultAdcData();

        this.model.setMultiplexedMetadata();

        this.driverService.setTopologyStatus();
        this.driverService.defaultRf();
        this.driverService.resetGain();
        this.driverService.resetRf();
        this.driverService.defaultCf();
        this.driverService.resetCf();

        this.driverSupplyService.calculateVSupply();

        this.hashDataService.setUrlParameter('model.rf');
        this.hashDataService.setUrlParameter('model.gain');
        this.hashDataService.setUrlParameter('model.vSupplyPos');
        this.hashDataService.setUrlParameter('model.vSupplyNeg');
        this.hashDataService.setUrlParameter('model.samplingRate');
        this.hashDataService.setUrlParameter('model.vref');
        this.hashDataService.setUrlParameter('model.multiplexed');
        this.hashDataService.setUrlParameter('model.rext');
        this.hashDataService.setUrlParameter('model.cext');
        this.hashDataService.setUrlParameter('model.vcm', this.model.isVcmFixed);
        this.hashDataService.setUrlParameter('model.showAdcVariant');
        this.hashDataService.setUrlParameter('model.showDriverVariant');
        this.hashDataService.setUrlParameter('sigmaDeltaData.mclk');

        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'mclkDiv');
        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'powerMode');
        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'filterType');
        this.hashDataService.setUrlParameterWithId('sigmaDeltaData', 'decimationRate');

        this.hashDataService.setUrlParameterWithSelectedPart('driver');
        this.hashDataService.setUrlParameterWithSelectedPart('adc');

        this.hashDataService.setUrlParameterWithSelectedId('sourceType');
        this.hashDataService.setUrlParameterWithSelectedId('topology');

        this.hashDataService.setUrlParameterForObject('showAdcVariants');

        if (this.updateAppData) this.updateAppData();

        this.analytics.setWebEvent(this.partData.toolName, 'Selected ADC', `${this.selectedPart}`);

        closeDialog();
    }

    @action adcChanged(adc) {
        this.selectedPart = adc;
    }
}
